import { ExternalLink, Stethoscope } from "@tamagui/lucide-icons";
import { Linking } from "react-native";
import { ScrollView, Sheet, XStack, YStack } from "tamagui";

import { useGetAgeVerificationStatus } from "@/api/generated";
import type { AgeVerificationStatusResponse } from "@/api/generated/models";
import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { Heading, Link, Paragraph, Text } from "@/components/Typography";
import { shortBrandName } from "@/config/brand";
import { useAuthStore } from "@/store";
import { getShadow } from "@/theme/helpers";
import { Separator } from "./Layout";

const BLUEBERRY_PEDIATRICS_URL = "https://app.blueberrypediatrics.com/bbjoin";

type Resource = {
  title: string;
  description: string;
  url: string;
};

const RESOURCES: Resource[] = [
  {
    title: "Planned Parenthood Direct",
    description:
      "Confidential sexual-health info and virtual visits for things like birth control, STI testing, and general health questions.",
    url: "https://www.plannedparenthooddirect.org/",
  },
  {
    title: "Brightline",
    description:
      "Online mental-health care made for kids and teens — get matched with a coach or licensed therapist for support.",
    url: "https://www.hellobrightline.com/",
  },
  {
    title: "Teen Line",
    description:
      "Text or call to talk with another teen who listens and gets it. A safe space for whatever's on your mind.",
    url: "https://www.teenline.org/",
  },
  {
    title: "KidsHealth for Teens",
    description:
      "Doctor-approved info about your body, emotions, and relationships — written just for teens.",
    url: "https://kidshealth.org/en/teens/",
  },
  {
    title: "988 Lifeline",
    description:
      "If you ever feel unsafe or in crisis, you can call or text 988 any time to connect with trained counselors who care and listen.",
    url: "https://988lifeline.org/",
  },
];

const BlueberryPediatricsCard = () => (
  <YStack
    backgroundColor="$fillCallout"
    borderWidth={1}
    borderColor="$strokeNeutral"
    borderRadius="$200"
    padding="$300"
    gap="$200"
    {...getShadow()}
  >
    <XStack alignItems="center" gap="$200">
      <YStack
        backgroundColor="$fillCuraiSubtle"
        borderRadius="$1000"
        padding="$150"
      >
        <Stethoscope size="$300" color="$iconBase" />
      </YStack>
      <YStack flex={1}>
        <Text size="$sm" color="$textSecondary">
          {shortBrandName} care partner
        </Text>
        <Heading size="$md">Blueberry Pediatrics</Heading>
      </YStack>
    </XStack>
    <Paragraph>
      {shortBrandName} works directly with Blueberry Pediatrics to care for
      patients under 18. Talk to a pediatrician from home — ask a parent or
      guardian to help you sign up.
    </Paragraph>
    <Button
      mode="primary"
      iconAfter={ExternalLink}
      dataTrackingId="under-18-blueberry-pediatrics"
      onPress={() => Linking.openURL(BLUEBERRY_PEDIATRICS_URL)}
    >
      Get started with Blueberry
    </Button>
  </YStack>
);

const Content = () => (
  <YStack
    paddingHorizontal="$200"
    paddingTop="$800"
    paddingBottom="$800"
    $lg={{ padding: "$400" }}
    gap="$300"
  >
    <YStack gap="$200">
      <Heading size="$xl">You must be 18 to use {shortBrandName}</Heading>
      <Text color="$textSecondary">
        {shortBrandName} is for adults only, but you're not alone.
      </Text>
    </YStack>

    <BlueberryPediatricsCard />

    <Text color="$textSecondary">
      Here are some other trusted resources that may help.
    </Text>

    <YStack gap="$300">
      {RESOURCES.map((resource) => (
        <YStack key={resource.title} gap="$100">
          <Link href={resource.url} external>
            {resource.title}
          </Link>
          <Text color="$textSecondary">{resource.description}</Text>
        </YStack>
      ))}
    </YStack>

    <Separator borderColor="$strokeNeutral" />

    <Text color="$textSecondary">
      If you feel you have been blocked in error or would like to download any
      medical records created through Curai Health, please{" "}
      <Link href="https://www.curaihealth.com/support" external>
        contact support here.
      </Link>
    </Text>
  </YStack>
);

export default function Under18BlockingOverlay() {
  const { isAuthInitialized } = useAuthStore();
  const { data: ageVerificationStatus } = useGetAgeVerificationStatus({
    query: {
      enabled: isAuthInitialized,
      select: (data: AgeVerificationStatusResponse) => {
        return {
          shouldShowUnder18BlockingOverlay: data.is_user_age_prohibited,
        };
      },
    },
  });

  const handleOverlayPress = (event: any) => {
    event.stopPropagation();
    event.preventDefault?.();
    return false;
  };

  if (!ageVerificationStatus?.shouldShowUnder18BlockingOverlay) {
    return null;
  }

  return (
    <Dialog open={true} modal disableRemoveScroll>
      <Dialog.Adapt when="maxLg">
        <Sheet
          modal
          open={true}
          disableDrag
          dismissOnSnapToBottom={false}
          snapPoints={[98]}
        >
          <Sheet.Frame flex={1}>
            <ScrollView flex={1}>
              <Content />
            </ScrollView>
          </Sheet.Frame>
          <Sheet.Overlay
            backgroundColor="$fillOverlay"
            backdropFilter="blur(8px)"
            onPress={handleOverlayPress}
          />
        </Sheet>
      </Dialog.Adapt>
      <Dialog.Portal>
        <Dialog.Overlay
          backgroundColor="$fillOverlay"
          backdropFilter="blur(8px)"
          onPress={handleOverlayPress}
        />
        <Dialog.FocusScope trapped loop>
          <Dialog.Content
            $lg={{
              maxWidth: "$constraint",
              width: "100%",
              minHeight: "50vh",
              minWidth: 500,
            }}
            minWidth="100%"
            maxHeight="90vh"
            padding={0}
            overflow="hidden"
          >
            <ScrollView>
              <Content />
            </ScrollView>
          </Dialog.Content>
        </Dialog.FocusScope>
      </Dialog.Portal>
    </Dialog>
  );
}
